import { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { AuthLayout } from '../../components/AuthLayout';
import { getLegalDocuments, type LegalDocumentKind } from '../../api/signup';
import classes from './LegalDocumentPage.module.css';

/** Headings start with «#», and blank lines part the paragraphs. The text is shown, never run. */
const blocks = (content: string) =>
  content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const heading = /^(#{1,3})\s+(.*)$/.exec(block);
      return heading ? { heading: heading[2] } : { text: block };
    });

/**
 * The current terms or data processing agreement, exactly as the user accepts them, with the
 * version and the hash that the acceptance records. A PDF is linked to, and its hash is of its bytes.
 */
export const LegalDocumentPage = ({ kind }: { kind: LegalDocumentKind }): ReactElement => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'en' ? 'en' : 'nb';
  const { data, isLoading, error } = useQuery({
    queryKey: ['legal-documents', 'current', language],
    queryFn: () => getLegalDocuments(language),
  });
  const document = data?.find((candidate) => candidate.kind === kind);
  // A PDF is kept in each language. The other one is listed too, with its own hash.
  const otherLanguage = language === 'en' ? 'nb' : 'en';
  const { data: otherData } = useQuery({
    queryKey: ['legal-documents', 'current', otherLanguage],
    queryFn: () => getLegalDocuments(otherLanguage),
    enabled: !!document?.url,
  });
  const other = otherData?.find(
    (candidate) => candidate.kind === kind && candidate.url && candidate.language !== document?.language,
  );
  const K = 'auth.legal';

  return (
    <AuthLayout wide title={t(`${K}.${kind}`)}>
      {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
      {(error || (data && !document)) && (
        <Alert data-color="warning">
          <Paragraph>{t(`${K}.missing`)}</Paragraph>
        </Alert>
      )}
      {document && (
        <article className={classes.document}>
          <Paragraph data-size="sm" className={classes.meta}>
            {t(`${K}.version`, {
              version: document.version,
              date: new Date(document.publishedAt).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'nb-NO'),
            })}
          </Paragraph>
          {document.url && (
            <Paragraph>
              <a href={document.url} target="_blank" rel="noreferrer" className="ds-link">
                {t(`${K}.openPdf`, { format: t(`${K}.pdfFormat.${document.language}`) })}
              </a>
            </Paragraph>
          )}
          {blocks(document.content).map((block, index) =>
            'heading' in block ? (
              <Heading key={index} level={2} data-size="xs">
                {block.heading}
              </Heading>
            ) : (
              <Paragraph key={index} className={classes.text}>
                {block.text}
              </Paragraph>
            ),
          )}
          <Paragraph data-size="xs" className={classes.meta}>
            {t(`${K}.hash`)} <code className={classes.hash}>{document.sha256}</code>
          </Paragraph>
          {other?.url && (
            <>
              <Heading level={2} data-size="2xs">
                {t(`${K}.otherLanguage.${other.language}`)}
              </Heading>
              <Paragraph data-size="sm" className={classes.meta}>
                {t(`${K}.version`, {
                  version: other.version,
                  date: new Date(other.publishedAt).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'nb-NO'),
                })}
              </Paragraph>
              <Paragraph>
                <a href={other.url} target="_blank" rel="noreferrer" className="ds-link">
                  {t(`${K}.openPdf`, { format: t(`${K}.pdfFormat.${other.language}`) })}
                </a>
              </Paragraph>
              <Paragraph data-size="xs" className={classes.meta}>
                {t(`${K}.hash`)} <code className={classes.hash}>{other.sha256}</code>
              </Paragraph>
            </>
          )}
        </article>
      )}
    </AuthLayout>
  );
};
